import React from 'react';
import { toast } from 'react-toastify';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { request } from '@mairistem/tools';
import { Dimmer, Loader } from '@mairistem/composants';

import { PARAM_NUMERISES } from '@/constants';
import { baseUrlApi } from '@/routes';
import { useEntiteStore } from '@/zustand';

interface BlocGraphiqueStatistiqueProps {
  anneeMax: number;
  anneeMin: number;
}

// Ordre custom des éléments dans le tooltip
const tooltipOrder: Record<string, number> = {
  Naissances: 1,
  Reconnaissances: 2,
  Mariages: 3,
  Décès: 4,
  Mentions: 5,
  Total: 6,
};

const getSeriesOrder = (seriesName: string) =>
  tooltipOrder[seriesName] ?? Number.MAX_SAFE_INTEGER;

const BlocGraphiqueStatistique = (props: BlocGraphiqueStatistiqueProps) => {
  const { anneeMin, anneeMax } = props;

  const [statistiques, setStatistiques] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const blocked = useEntiteStore((state) => state.blocked);

  React.useEffect(() => {
    if (!blocked) {
      setLoading(true);
      request
        .get(
          `${baseUrlApi}/numerisation-actes/statistiques/${anneeMin}/${anneeMax}?${PARAM_NUMERISES}`,
        )
        .then((response: any) => {
          setStatistiques(response);
        })
        .catch(() => {
          setStatistiques(null);
          toast.error(
            `Impossible de charger les statistiques entre les années ${anneeMin} et ${anneeMax}`,
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [anneeMin, anneeMax, blocked]);

  const data = React.useMemo(() => {
    if (!statistiques || typeof statistiques !== 'object') {
      return [];
    }

    const years = Object.keys(statistiques).sort();
    return years.map((year) => {
      const yearData = statistiques[year];
      const naissances = yearData.Naissances || 0;
      const reconnaissances = yearData.Reconnaissances || 0;
      const mariages = yearData.Mariages || 0;
      const deces = yearData.Décès || 0;
      const mentions = yearData.Mentions || 0;
      const total = naissances + reconnaissances + mariages + deces + mentions;

      return {
        annee: year,
        naissances,
        reconnaissances,
        mariages,
        deces,
        mentions,
        total,
      };
    });
  }, [statistiques]);

  return (
    <div
      style={{
        marginLeft: 0,
        height: '100%',
      }}
    >
      {loading && (
        <Dimmer active inverted>
          <Loader content="Chargement des statistiques..." />
        </Dimmer>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="annee"
            tick={{ fill: '#666' }}
            tickLine={{ stroke: '#ccc' }}
          />
          <YAxis tick={{ fill: '#666' }} tickLine={{ stroke: '#ccc' }} />
          <Tooltip
            itemSorter={(item) => {
              if (typeof item === 'string') {
                return getSeriesOrder(item);
              }

              const seriesName = String(item.value || item.dataKey || '');
              return getSeriesOrder(seriesName);
            }}
            isAnimationActive={false}
            allowEscapeViewBox={{ x: false, y: false }}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
            }}
          />
          <Legend
            itemSorter={(item) => {
              if (typeof item === 'string') {
                return getSeriesOrder(item);
              }

              const seriesName = String(item.value || item.dataKey || '');
              return getSeriesOrder(seriesName);
            }}
            wrapperStyle={{
              paddingTop: '20px',
            }}
            iconType="line"
          />

          <Line
            type="monotone"
            dataKey="naissances"
            stroke="#2185d0"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#2185d0', r: 4 }}
            name="Naissances"
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="reconnaissances"
            stroke="#21ba45"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#21ba45', r: 4 }}
            name="Reconnaissances"
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="mariages"
            stroke="#e03997"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#e03997', r: 4 }}
            name="Mariages"
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="deces"
            stroke="#767676"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#767676', r: 4 }}
            name="Décès"
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="mentions"
            stroke="#f2711c"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#f2711c', r: 4 }}
            name="Mentions"
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="total"
            stroke="#000000"
            strokeWidth={2}
            dot={{ fill: '#000000', r: 4 }}
            name="Total"
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BlocGraphiqueStatistique;
